import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { LivePickupOut, LivePickupsOut, LiveStoreOut } from '../api/types'
import { Kpi } from '../components/Kpi'
import { PageHeader } from '../components/Layout'
import { Panel } from '../components/States'
import { count, formatTime } from '../lib/format'

// The app's clock turns amber at 5 minutes (mobile/lib/widgets/wait_clock.dart);
// the server sends when it turns red.
const AMBER_SECONDS = 300

function clock(seconds: number): string {
  const whole = Math.floor(seconds)
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`
}

function WaitClock({ seconds, redSeconds }: { seconds: number; redSeconds: number }) {
  const tone = seconds >= redSeconds ? 'red' : seconds >= AMBER_SECONDS ? 'amber' : 'green'
  return (
    <span className={`chip ${tone}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {clock(seconds)}
    </span>
  )
}

export function PickupLivePage() {
  // The same pace as the warehouse phones. Nothing is fetched while the tab is
  // hidden.
  const query = useQuery({
    queryKey: ['pickups-live'],
    queryFn: () => api.get<LivePickupsOut>('/analytics/pickups/live'),
    refetchInterval: 10_000,
  })

  // Clocks count on from the server's figures, so a wrong computer clock can't
  // skew them.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const data = query.data
  const elapsed = Math.max(0, (now - query.dataUpdatedAt) / 1000)
  const redSeconds = (data?.target_minutes ?? 10) * 60
  const pickups = data?.stores.flatMap((store) => store.pickups) ?? []
  const waiting = pickups.filter((pickup) => !pickup.ready).length
  const longest = pickups.reduce((most, pickup) => Math.max(most, pickup.waiting_seconds), 0)

  return (
    <>
      <PageHeader
        title="Live pickups"
        subtitle={
          query.dataUpdatedAt ? (
            <>Updated {formatTime(new Date(query.dataUpdatedAt).toISOString())}, refreshes every 10 seconds</>
          ) : null
        }
      />

      <div className="content stack">
        <Panel isLoading={query.isPending} error={query.error}>
          {data ? (
            <>
              <div className="grid cols-4">
                <Kpi label="Customers waiting" value={count(pickups.length)} foot="Across all stores" />
                <Kpi label="Waiting for warehouse dispatch" value={count(waiting)} />
                <Kpi label="Ready at the counter" value={count(pickups.length - waiting)} />
                <Kpi
                  label="Longest wait"
                  value={pickups.length ? <WaitClock seconds={longest + elapsed} redSeconds={redSeconds} /> : '—'}
                />
              </div>

              <div className="grid cols-2">
                {data.stores.map((store) => (
                  <StoreCard key={store.store} store={store} elapsed={elapsed} redSeconds={redSeconds} />
                ))}
              </div>
            </>
          ) : null}
        </Panel>
      </div>
    </>
  )
}

function StoreCard({
  store,
  elapsed,
  redSeconds,
}: {
  store: LiveStoreOut
  elapsed: number
  redSeconds: number
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h2>{store.store}</h2>
        <div className="row" style={{ alignItems: 'center', gap: 8 }}>
          {store.waiting_dispatch ? <span className="chip blue">{count(store.waiting_dispatch)} waiting</span> : null}
          {store.ready ? <span className="chip teal">{count(store.ready)} ready</span> : null}
        </div>
      </div>
      <div className="card-body flush">
        <Panel isLoading={false} error={null} isEmpty={store.pickups.length === 0} emptyText="No customers waiting.">
          <div className="table-wrap">
            <table>
              <tbody>
                {store.pickups.map((pickup) => (
                  <PickupRow key={pickup.id} pickup={pickup} elapsed={elapsed} redSeconds={redSeconds} />
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  )
}

function PickupRow({
  pickup,
  elapsed,
  redSeconds,
}: {
  pickup: LivePickupOut
  elapsed: number
  redSeconds: number
}) {
  const items = pickup.items.map((item) => `${item.name} × ${item.qty}`).join(', ')
  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600 }}>{pickup.customer}</div>
        <div className="cell-sub">
          <Link to={`/invoices/${pickup.invoice_id}`}>{pickup.invoice_number}</Link>
          {pickup.requested_by ? ` · requested by ${pickup.requested_by}` : null}
        </div>
        <div className="cell-sub">{items}</div>
        <div style={{ marginTop: 4 }}>
          <span className={`chip ${pickup.ready ? 'teal' : 'blue'}`}>
            {pickup.ready ? 'Ready at the counter' : 'Waiting for warehouse dispatch'}
          </span>
        </div>
      </td>
      <td className="num" style={{ verticalAlign: 'top' }}>
        <WaitClock seconds={pickup.waiting_seconds + elapsed} redSeconds={redSeconds} />
      </td>
    </tr>
  )
}
